/**
=========================================================
* Otis Admin PRO - v2.0.1
=========================================================

* Product Page: https://material-ui.com/store/items/otis-admin-pro-material-dashboard-react/
* Copyright 2022 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

/**
  All of the routes for the Otis Admin PRO React are added here,
  You can add a new route, customize the routes and delete the routes here.

  Once you add a new route on this file it will be visible automatically on
  the Sidenav.

  For adding a new route you can follow the existing routes in the routes array.
  1. The `type` key with the `collapse` value is used for a route.
  2. The `type` key with the `title` value is used for a title inside the Sidenav.
  3. The `type` key with the `divider` value is used for a divider between Sidenav items.
  4. The `name` key is used for the name of the route on the Sidenav.
  5. The `key` key is used for the key of the route (It will help you with the key prop inside a loop).
  6. The `icon` key is used for the icon of the route on the Sidenav, you have to add a node.
  7. The `collapse` key is used for making a collapsible item on the Sidenav that contains other routes
  inside (nested routes), you need to pass the nested routes inside an array as a value for the `collapse` key.
  8. The `route` key is used to store the route location which is used for the react router.
  9. The `href` key is used to store the external links location.
  10. The `title` key is only for the item with the type of `title` and its used for the title text on the Sidenav.
  10. The `component` key is used to store the component of its route.
*/

// Otis Admin PRO React layouts
import { Invoices } from 'features/invoices/Invoices';
import { InvoicePreview } from 'features/InvoicePreview/InvoicePreview';

// @mui icons
import Icon from '@mui/material/Icon';

// Images
import { InvoiceEditor } from 'features/invoiceEditor/invoiceEditor';
import LogonPage from 'features/logon/logonPage';
import navigationPaths from './engine/navigationPaths';
import Inventory2OutlinedIcon from '@mui/icons-material/Inventory2Outlined';
import SettingsOutlinedIcon from '@mui/icons-material/SettingsOutlined';
import DashboardOutlinedIcon from '@mui/icons-material/DashboardOutlined';

import PointOfSaleOutlinedIcon from '@mui/icons-material/PointOfSaleOutlined';

import UserProfile from 'pages/userProfile/userProfile';
import PersonIcon from '@mui/icons-material/AccountCircle';
import { RegistrationPage } from 'features/registration/registrationPage';

// Costs
import { Proformas } from 'features/proformas/Proformas';
import ProformaEditor from 'features/proformas/ProformaEditor';
import { ProformaPreview } from 'features/proformaPreview/ProformaPreview';
import ResetPasswordPage from 'features/logon/resetPasswordPage';
import ConfirmEmail from 'features/logon/emailConfirmation';

//Correction Ivoices
import CorrectionInvoiceEditor from 'features/correctionInvoices/correctionInvoiceEditor';
import { CorrectionInvoicePreview } from 'features/correctionInvoicePreview/CorrectionInvoicePreview';

import {
  Contractors,
  CostForm,
  CostList,
  CostPreview,
  Dashboard,
  ForgotPassword,
  Settings,
  Autologin,
  DashboardFluent,
  Exports,
  ViewEditor,
} from 'pages';
import { Pricing } from 'pages/pricing/pricing';
import LinkedInPage from 'pages/linkedIn/linkedIn';
import { OEM } from 'utility/OEMProvider';
import { FolderSharedOutlined } from '@mui/icons-material';

import { config } from 'oems/config';
import { Inventory } from 'pages/Inventory/Inventory';

const dashboardRoutes = (dashboardSetup) => {
  return {
    type: 'main',
    collapse: [
      {
        type: 'hidden',
        name: 'Dashboard Fluent',
        key: 'dashboard-fluent',
        route: navigationPaths().DashboardFluent,
        component: <DashboardFluent />,
      },
      {
        type: 'hidden',
        name: 'Dashboard',
        key: 'dashboard',
        route: navigationPaths().Dashboard,
        component: <DashboardFluent />,
      },
      {
        type: 'hidden',
        name: 'View Editor',
        key: 'view-editor',
        route: navigationPaths().ViewEditor,
        component: <ViewEditor />,
      },
    ],
    name: 'Dashboard',
    key: 'dashboard-collapse',
    route: navigationPaths().Dashboard,
    icon: <DashboardOutlinedIcon fontSize="medium" />,
    component: <DashboardFluent />,
    noCollapse: true,
  };
};

const profileRoutes = (username, profileSetup) => {
  const profile = {
    name: username,
    key: '',
    icon: <PersonIcon />,
    type: 'hidden',
    collapse: [],
  };

  if (profileSetup.childKeys.includes('profile')) {
    profile.collapse.push({
      name: 'Profil',
      key: 'profile',
      route: navigationPaths().UserProfile,
      component: <UserProfile />,
      type: 'hidden',
    });
  }

  if (profileSetup.childKeys.includes('login')) {
    profile.collapse.push({
      type: 'hidden',
      name: 'Zaloguj',
      key: 'login',
      noLayout: true,
      route: navigationPaths().LogIn,
      component: <LogonPage />,
    });
  }

  if (profileSetup.childKeys.includes('register')) {
    profile.collapse.push({
      type: 'hidden',
      name: 'Zarejestruj',
      key: 'register',
      noLayout: true,
      route: navigationPaths().Register,
      component: <RegistrationPage />,
    });
  }

  if (profileSetup.childKeys.includes('forgot-password')) {
    profile.collapse.push({
      type: 'hidden',
      name: 'Zapomniałeś hasło?',
      key: 'forgot-password',
      noLayout: true,
      route: navigationPaths().ForgotPassword,
      component: <ForgotPassword />,
    });
  }

  if (profileSetup.childKeys.includes('reset-password')) {
    profile.collapse.push({
      type: 'hidden',
      name: 'Odzyskaj hasło',
      key: 'reset-password',
      route: navigationPaths().ResetPassword,
      component: <ResetPasswordPage />,
    });
  }

  if (profileSetup.childKeys.includes('register-confirmation')) {
    profile.collapse.push({
      type: 'hidden',
      name: 'Potwierdź rejestrację',
      key: 'register-confirmation',
      route: navigationPaths().RegisterConfirmation,
      component: <ConfirmEmail />,
    });
  }
  if (profileSetup.childKeys.includes('autologin')) {
    profile.collapse.push({
      type: 'hidden',
      name: 'Autologowanie',
      key: 'autologin',
      route: navigationPaths().Autologin,
      component: <Autologin />,
    });
  }

  return profile;
};

const accountingRoutes = (accountingSetup) => {
  const accounting = {
    type: 'title',
    title: 'Księgowość',
    key: 'accounting',
  };

  return accounting;
};

const salesRoutes = (salesSetup) => {
  const sales = {
    type: 'collapse',
    name: 'Sprzedaż',
    key: 'sales',
    icon: <Icon fontSize="medium">article</Icon>,
    collapse: [],
  };

  if (salesSetup.childKeys.includes('invoices-form')) {
    sales.collapse.push({
      name: 'Nowa Faktura',
      key: 'invoices-form',
      route: navigationPaths().InvoicesForm,
      component: <InvoiceEditor />,
      state: { mode: 'create' },
      cssBeforeName: '+',
      purchasePlanLimits: ['documentLimitExceeded'],
    });
  }

  if (salesSetup.childKeys.includes('invoices')) {
    sales.collapse.push({
      name: 'Lista Faktur',
      key: 'invoices',
      route: navigationPaths().Invoices,
      component: <Invoices />,
      cssBeforeName: '≡',
    });
  }

  if (salesSetup.childKeys.includes('invoices-preview')) {
    sales.collapse.push({
      type: 'hidden',
      name: 'Podgląd faktury',
      key: 'invoices-preview',
      route: navigationPaths().InvoicesPreview,
      component: <InvoicePreview />,
    });
  }

  if (salesSetup.childKeys.includes('correction-invoices-preview')) {
    sales.collapse.push({
      type: 'hidden',
      name: 'Podgląd korekty faktury',
      key: 'correction-invoices-preview',
      route: navigationPaths().CorrectionInvoicesPreview,
      component: <CorrectionInvoicePreview />,
    });
  }

  if (salesSetup.childKeys.includes('proformas-preview')) {
    sales.collapse.push({
      type: 'hidden',
      name: 'Podgląd proformy',
      key: 'proformas-preview',
      route: navigationPaths().ProformasPreview,
      component: <ProformaPreview />,
    });
  }

  if (salesSetup.childKeys.includes('proformas-form')) {
    sales.collapse.push({
      name: 'Nowa Pro-Forma',
      key: 'proformas-form',
      route: navigationPaths().ProformasForm,
      component: <ProformaEditor />,
      state: { mode: 'create' },
      cssBeforeName: '+',
      purchasePlanLimits: ['documentLimitExceeded'],
    });
  }

  if (salesSetup.childKeys.includes('proformas')) {
    sales.collapse.push({
      name: 'Lista Pro-Form',
      key: 'proformas',
      route: navigationPaths().Proformas,
      component: <Proformas />,
      cssBeforeName: '≡',
    });
  }

  if (salesSetup.childKeys.includes('corrections-forms')) {
    sales.collapse.push({
      name: 'Nowa faktura korygująca',
      key: 'corrections-forms',
      type: 'hidden',
      route: navigationPaths().CorrectionInvoicesForm,
      component: <CorrectionInvoiceEditor />,
      cssBeforeName: '≡',
    });
  }

  return sales;
};

const costInvoicesRoutes = (costInvoicesSetup) => {
  const costInvoices = {
    type: 'collapse',
    name: 'Koszty',
    key: 'cost-invoices',
    icon: <PointOfSaleOutlinedIcon fontSize="medium" />,
    collapse: [],
  };

  if (costInvoicesSetup.childKeys.includes('cost-invoices-form')) {
    costInvoices.collapse.push({
      name: 'Nowy Koszt',
      key: 'cost-invoices-form',
      route: navigationPaths().CostInvoicesForm,
      component: <CostForm />,
      cssBeforeName: '+',
    });
  }

  if (costInvoicesSetup.childKeys.includes('cost-invoices-form-edit')) {
    costInvoices.collapse.push({
      name: 'Edycja Kosztu',
      type: 'hidden',
      key: 'cost-invoices-form-edit',
      route: navigationPaths().CostInvoicesFormEdit,
      component: <CostForm />,
    });
  }

  if (costInvoicesSetup.childKeys.includes('cost-invoices-form-preview')) {
    costInvoices.collapse.push({
      name: 'Podgląd Kosztu',
      type: 'hidden',
      key: 'cost-invoices-form-preview',
      route: navigationPaths().CostInvoicesFormPreview,
      component: <CostPreview />,
    });
  }

  if (costInvoicesSetup.childKeys.includes('cost-invoices')) {
    costInvoices.collapse.push({
      name: 'Lista Kosztów',
      key: 'cost-invoices',
      route: navigationPaths().CostInvoices,
      component: <CostList />,
      cssBeforeName: '≡',
    });
  }

  return costInvoices;
};

const warehouseRoutes = (warehouseSetup) => {
  const warehouse = { 
    type: 'title', 
    title: 'Kartoteka Produktów',
    key: 'inventory'
  };

  return warehouse;
};

const inventoryRoutes = (warehouseSetup) => {
  if (warehouseSetup.childKeys.includes('inventoryContact')) {
    return {
      type: 'main',
      collapse: false,
      name: 'Kontakt',
      key: 'contact322',
      href: OEM().ContactUrl,
      icon: <Inventory2OutlinedIcon fontSize="medium" />,
      newWindow: true,
    };
  };

  const inventory = {
    type: 'collapse',
    name: 'Kartoteka',
    key: 'warehouse',
    icon: <Inventory2OutlinedIcon fontSize="medium" />,
    collapse: []
  };

  if (warehouseSetup.childKeys.includes('products')) {
    inventory.collapse.push({
      name: 'Lista produktów',
      key: 'inventory',
      route: navigationPaths().Inventory,
      cssBeforeName: '≡',
      component: <Inventory />
    });
    inventory.component = <Inventory />;
  };

  return inventory;
};

const contractorsRoutes = (contractorsSetup) => {
  return [
    { type: 'title', title: 'Kontrahenci' },
    {
      key: 'contractors',
      name: 'Kontrahenci',
      type: 'collapse',
      icon: <FolderSharedOutlined fontSize="medium" />,
      collapse: [
        {
          name: 'Lista kontrahentów',
          key: 'contractors',
          route: navigationPaths().Contractors,
          cssBeforeName: '≡',
          component: <Contractors />,
        },
      ],
      component: <Contractors />,
    },
  ];
};

const settingsRoutes = (settingsSetup, isAdministrator, isRegistered) => {
  const settings = {
    type: 'collapse',
    name: 'Ustawienia',
    key: 'settings-collapse',
    icon: <SettingsOutlinedIcon fontSize="medium" />,
    collapse: [],
  };

  if (settingsSetup.childKeys.includes('settings')) {
    settings.collapse.push({
      name: 'Ustawienia',
      key: 'settings',
      icon: <SettingsOutlinedIcon fontSize="medium" />,
      route: navigationPaths().Settings,
      component: <Settings />,
    });
  }

  if (settingsSetup.childKeys.includes('settings-exports')) {
    settings.collapse.push({
      name: 'Eksporty',
      key: 'settings-exports',
      route: navigationPaths().Exports,
      component: <Exports />,
    });
  }

  if (settingsSetup.childKeys.includes('settings-pricing')) {
    settings.collapse.push({
      name: 'Plany cenowe',
      key: 'settings-pricing',
      route: navigationPaths().Pricing,
      component: <Pricing />,
    });
  }

  if (settingsSetup.childKeys.includes('linkedin-login')) {
    settings.collapse.push({
      name: 'LinkedIn login callback',
      key: 'linkedin-login',
      type: 'hidden',
      route: navigationPaths().LinkedIn,
      component: <LinkedInPage />,
    });
  }

  return settings;
};

export default function getAllRoutes(username, isAdministrator, isRegistered) {
  const dashboardSetup = config.dashboard;
  const profileSetup = config.profile;
  const accountingSetup = config.accounting;
  const salesSetup = config.sales;
  const costInvoicesSetup = config.costInvoices;
  const warehouseSetup = config.warehouse;
  const contractorsSetup = config.contractors;
  const settingsSetup = config.settings;

  var routes = [];

  if (dashboardSetup !== undefined && dashboardSetup.show === true) {
    routes.push(dashboardRoutes(dashboardSetup));
  }

  if (profileSetup !== undefined && profileSetup.show === true) {
    routes.push(profileRoutes(username, profileSetup));
  }

  if (accountingSetup !== undefined && accountingSetup.show === true) {
    routes.push(accountingRoutes(accountingSetup));
  }

  if (salesSetup !== undefined && salesSetup.show === true) {
    routes.push(salesRoutes(salesSetup));
  }

  if (costInvoicesSetup !== undefined && costInvoicesSetup.show === true) {
    routes.push(costInvoicesRoutes(costInvoicesSetup));
  }

  if (warehouseSetup !== undefined && warehouseSetup.show === true) {
    routes = routes.concat(warehouseRoutes(warehouseSetup));
  }

  if (warehouseSetup !== undefined && warehouseSetup.show === true) {
    routes = routes.concat(inventoryRoutes(warehouseSetup));
  }

  if (contractorsSetup !== undefined && contractorsSetup.show === true) {
    routes = routes.concat(contractorsRoutes(contractorsSetup));
  }

  if (settingsSetup !== undefined && settingsSetup.show === true) {
    routes.push({ type: 'divider' });
    routes.push(settingsRoutes(settingsSetup, isAdministrator, isRegistered));
  }

  return routes;
}
